import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { ArrowLeft, ArrowRight, Sparkles, Loader2, Mail, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DiagnosticPage() {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sessionId] = useState(() => uuidv4());
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const isFrench = i18n.language === 'fr';

  // Validation email simple
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  useEffect(() => {
    fetchQuestions();
  }, [i18n.language]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions?lang=${i18n.language}`);
      setQuestions(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error(t('diagnostic.error'));
      setLoading(false);
    }
  };

  const handleSelectOption = (questionId, category, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { question_id: questionId, category, value }
    }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmitClick = () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error(t('diagnostic.answerAll'));
      return;
    }

    // Si utilisateur connecté, soumettre directement
    if (user) {
      submitDiagnostic(user.email);
    } else {
      // Sinon, demander l'email
      setShowEmailModal(true);
    }
  };

  const submitDiagnostic = async (email) => {
    // Validation de l'email
    if (!email || !isValidEmail(email)) {
      setEmailError(isFrench ? "Veuillez entrer un email valide" : "Please enter a valid email");
      return;
    }
    
    setSubmitting(true);
    setEmailError("");
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API}/diagnostic/submit`, {
        session_id: sessionId,
        answers: Object.values(answers),
        language: i18n.language,
        user_email: email
      }, { headers });
      
      setShowEmailModal(false);
      
      // Vérifier que l'email a été envoyé
      if (response.data.email_sent) {
        toast.success(isFrench 
          ? "Vos résultats ont été envoyés à votre email !" 
          : "Your results have been sent to your email!");
      }
      
      toast.success(t('diagnostic.completed'));
      navigate(`/results/${sessionId}`);
    } catch (error) {
      console.error("Error submitting diagnostic:", error);
      const errorMsg = error.response?.data?.detail || t('diagnostic.error');
      setEmailError(errorMsg);
      toast.error(errorMsg);
      setSubmitting(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const isLastQuestion = currentIndex === questions.length - 1;
  const isAnswered = currentQuestion && answers[currentQuestion.id];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#2F4F4F] animate-spin mx-auto mb-4" />
          <p className="text-[#5C5C5C]">{t('diagnostic.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header with progress */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="flex items-center gap-2 text-[#2F4F4F] hover:opacity-80 transition-opacity" data-testid="back-home-link">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">{t('nav.back')}</span>
            </Link>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#2F4F4F]" />
              <span className="font-heading text-lg text-[#2F4F4F]">Épanoui</span>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <span className="text-sm text-[#8A8A8A]">
                {t('diagnostic.question')} {currentIndex + 1}/{questions.length}
              </span>
            </div>
          </div>
          <Progress value={progress} className="h-2 bg-gray-100" data-testid="progress-bar" />
        </div>
      </header>

      {/* Main content */}
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          {currentQuestion && (
            <div className="animate-fade-in" key={currentQuestion.id}>
              {/* Category badge */}
              <div className="mb-6">
                <span className="inline-flex px-4 py-2 rounded-full bg-[#A8C3A0]/20 text-[#2F4F4F] text-sm font-medium">
                  {t(`categories.${currentQuestion.category}`)}
                </span>
              </div>

              {/* Question */}
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#2D2D2D] mb-8 leading-relaxed" data-testid="question-text">
                {currentQuestion.text}
              </h2>

              {/* Options */}
              <div className="space-y-4 mb-12">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectOption(currentQuestion.id, currentQuestion.category, option.value)}
                    className={`option-card ${answers[currentQuestion.id]?.value === option.value ? 'selected' : ''}`}
                    data-testid={`option-${idx}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                        answers[currentQuestion.id]?.value === option.value 
                          ? 'border-[#2F4F4F] bg-[#2F4F4F]' 
                          : 'border-gray-300'
                      }`}>
                        {answers[currentQuestion.id]?.value === option.value && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <span className="text-[#2D2D2D] leading-relaxed">{option.text}</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                  className="rounded-full px-6 border-gray-200 text-[#5C5C5C]"
                  data-testid="prev-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('diagnostic.previous')}
                </Button>

                {isLastQuestion ? (
                  <Button
                    onClick={handleSubmitClick}
                    disabled={!isAnswered || submitting}
                    className="rounded-full px-8 bg-[#E2725B] hover:bg-[#E2725B]/90 text-white"
                    data-testid="submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('diagnostic.analyzing')}
                      </>
                    ) : (
                      <>
                        {t('diagnostic.seeResults')}
                        <Sparkles className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={!isAnswered}
                    className="rounded-full px-8 bg-[#2F4F4F] hover:bg-[#2F4F4F]/90 text-white"
                    data-testid="next-btn"
                  >
                    {t('diagnostic.next')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Email Modal pour utilisateurs non connectés */}
      <Dialog open={showEmailModal} onOpenChange={(open) => {
        if (!submitting) {
          setShowEmailModal(open);
          setEmailError("");
        }
      }}>
        <DialogContent className="sm:max-w-md bg-white" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-center">
              {isFrench ? "📧 Dernière étape !" : "📧 Last step!"}
            </DialogTitle>
            <DialogDescription className="text-center text-[#5C5C5C]">
              {isFrench 
                ? "Votre email est nécessaire pour recevoir vos résultats personnalisés."
                : "Your email is required to receive your personalized results."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#5C5C5C] text-center text-sm">
              {isFrench 
                ? "Vos résultats seront envoyés directement dans votre boîte mail."
                : "Your results will be sent directly to your inbox."}
            </p>
            
            {emailError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{emailError}</span>
              </div>
            )}
            
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A8A8A]" />
              <Input
                type="email"
                placeholder={isFrench ? "Votre email" : "Your email"}
                value={userEmail}
                onChange={(e) => {
                  setUserEmail(e.target.value);
                  setEmailError("");
                }}
                className={`pl-10 h-12 rounded-xl ${emailError ? 'border-red-300 focus:border-red-500' : ''}`}
                data-testid="email-input-modal"
                disabled={submitting}
              />
            </div>
            <Button
              onClick={() => submitDiagnostic(userEmail)}
              disabled={!userEmail || submitting}
              className="w-full h-12 rounded-full bg-[#E2725B] hover:bg-[#E2725B]/90 text-white"
              data-testid="submit-with-email"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {isFrench ? "Envoi en cours..." : "Sending..."}
                </>
              ) : (
                <>
                  {isFrench ? "Recevoir mes résultats" : "Receive my results"}
                  <Sparkles className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            <p className="text-xs text-center text-[#8A8A8A]">
              {isFrench 
                ? "⚠️ Chaque email ne peut être utilisé qu'une seule fois pour le diagnostic gratuit."
                : "⚠️ Each email can only be used once for the free diagnostic."}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
